import { useState, useCallback, useEffect, useRef } from "react";
import { Office, AppointmentDetails, FilterState } from "../../types";
import { OfficeService } from "../../services/OfficeService";
import { UserService } from "../../services/UserService";

export interface OfficeFilterProps {
  offices: Office[];
  selectedOfficeId: string | null;
  officeLoading: boolean;
  onOfficeChange: (officeId: string | null) => void;
}

type SearchRoomsFn = (
  details: AppointmentDetails,
  filters: FilterState,
  officeId?: string | null
) => Promise<void>;

export function useOfficeFilter(
  appointmentDetails: AppointmentDetails | null,
  filters: FilterState,
  searchRooms: SearchRoomsFn
): OfficeFilterProps {
  const [offices, setOffices] = useState<Office[]>([]);
  const [selectedOfficeId, setSelectedOfficeId] = useState<string | null>(null);
  const [officeLoading, setOfficeLoading] = useState(false);

  // Keep stable refs for the re-search callback
  const filtersRef = useRef(filters);
  const appointmentRef = useRef(appointmentDetails);
  const searchRoomsRef = useRef(searchRooms);

  useEffect(() => {
    filtersRef.current = filters;
    appointmentRef.current = appointmentDetails;
    searchRoomsRef.current = searchRooms;
  }, [filters, appointmentDetails, searchRooms]);

  // Fetch offices and user's default office on mount
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      setOfficeLoading(true);
      try {
        const [officeList, userOfficeId] = await Promise.all([
          OfficeService.getOffices(),
          UserService.getUserDefaultOfficeId(),
        ]);

        if (cancelled) return;

        const sorted = officeList.sort((a, b) => a.name.localeCompare(b.name));
        setOffices(sorted);

        // Pick office: user's day/default office if it exists in the list, otherwise first
        const matchedOffice = userOfficeId
          ? sorted.find((o) => o.id === userOfficeId)
          : null;
        const initialOfficeId = matchedOffice?.id ?? sorted[0]?.id ?? null;

        if (initialOfficeId) {
          setSelectedOfficeId(initialOfficeId);
          if (appointmentRef.current) {
            void searchRoomsRef.current(
              appointmentRef.current,
              filtersRef.current,
              initialOfficeId
            );
          }
        }
      } catch {
        // Silently fail — dropdown won't show
      } finally {
        if (!cancelled) {
          setOfficeLoading(false);
        }
      }
    };

    void init();

    return () => {
      cancelled = true;
    };
  }, []);

  // Re-search when user changes office
  const onOfficeChange = useCallback((officeId: string | null) => {
    setSelectedOfficeId(officeId);

    if (appointmentRef.current) {
      void searchRoomsRef.current(
        appointmentRef.current,
        filtersRef.current,
        officeId
      );
    }
  }, []);

  return {
    offices,
    selectedOfficeId,
    officeLoading,
    onOfficeChange,
  };
}
